import { UseFormReturn } from 'react-hook-form';
import type { FlowStatus } from '@/lib/api/email-flows/types';
import { stanNotify } from '@/lib';
import type { EmailFormData } from '../types/email.types';
import { emailFormSchemaDraft, emailFormSchemaPublish } from '../validations/emailForm';

interface UseEmailFormHandlersProps {
    formMethods: UseFormReturn<EmailFormData>;
    onSave: (data: EmailFormData, status: FlowStatus) => Promise<void>;
    isFirstTimeBasedEmail?: boolean;
}

interface UseEmailFormHandlersReturn {
    handleSaveAsDraft: () => void;
    handlePublish: () => void;
}

export function useEmailFormHandlers({
    formMethods,
    onSave,
    isFirstTimeBasedEmail = false,
}: UseEmailFormHandlersProps): UseEmailFormHandlersReturn {
    const { handleSubmit, getValues, setError } = formMethods;

    const showValidationError = () => {
        stanNotify({
            type: 'error',
            title: 'Wait up!',
            text: 'Please provide input for each field.',
        });
    };

    const handleSaveAsDraft = handleSubmit(async (data: EmailFormData) => {
        await onSave(data, 'draft');
    }, showValidationError);

    const handlePublish = async () => {
        const formData = getValues();

        // For first time-based emails, skip delay validation
        const schemaToUse = isFirstTimeBasedEmail ? emailFormSchemaDraft : emailFormSchemaPublish;

        // Validate using the publish schema
        const result = schemaToUse.safeParse(formData);

        if (!result.success) {
            // Set form errors to show field-level validation messages
            result.error.errors.forEach(error => {
                const path = error.path[0] as keyof EmailFormData;
                if (path) {
                    setError(path, {
                        type: 'manual',
                        message: error.message,
                    });
                }
            });
            showValidationError();
            return;
        }

        await onSave(result.data, 'published');
    };

    return {
        handleSaveAsDraft,
        handlePublish,
    };
}
